/**
 * Pre-router auth bootstrap — AUTH-12 / D-04 / D-05.
 *
 * Runs once per app boot, BEFORE `<RouterProvider>` mounts so route loaders
 * can read `context.auth.getSnapshot()` synchronously in `beforeLoad`. Without
 * this step the router races the refresh, causing redirect loops on hard
 * refresh of deep links (Pitfall H2).
 *
 * Branching (CC-01 / D-05):
 *   1. No `linkhr_session=1` cookie — user has never logged in (or logged out
 *      cleanly). Return UNAUTH_SNAPSHOT without hitting `/auth/refresh`. Saves
 *      ~50–100ms on every cold load of /login and other unauth routes.
 *      Temporary bridge: a refresh-token copy an earlier build left in
 *      localStorage counts as a reason to try the cookie anyway (the copy is
 *      never sent) — see the BRIDGE note in `refreshOnLoad`.
 *   2. Sentinel present → call `tryRefreshCrossTab` (reuses the cross-tab
 *      single-flight coordinator so if two tabs boot simultaneously only one
 *      actually fires the network call).
 *   3. Refresh succeeds → decode claims, schedule the proactive timer, return
 *      an authenticated snapshot.
 *   4. No verdict (no answer, or a 5xx) → retry on a doubling wait: inside the
 *      boot budget, or with `waitForServer` until the server answers.
 *   5. Refresh fails (401, family revoked, a throttle that outlived the boot
 *      budget, no verdict within the budget) → clear the store, return
 *      UNAUTH_SNAPSHOT. NEVER throws. NEVER redirects: route loaders own
 *      redirect decisions. The refresh cookie is left alone, so the next boot
 *      can still restore it.
 *
 * The `user` field on the returned snapshot is null — `/me` is a follow-up
 * fetch Plan 05 owns. Bootstrap surfaces only the JWT-claims-derivable shape.
 */
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { decodeJwtClaims } from "../tokens/jwt.js";
import { getAudience, tryRefreshCrossTabFull } from "../refresh/crossTabCoordinator.js";
import {
  REFRESH_NETWORK_ERROR,
  REFRESH_RATE_LIMITED,
  REFRESH_SERVER_ERROR,
  ensureCooldownMirrorInstalled,
  getRefreshCooldownUntil,
} from "../refresh/performRefresh.js";
import { readSessionSentinel } from "../refresh/sessionSentinel.js";
import { scheduleProactiveRefresh } from "../refresh/proactiveTimer.js";
import { loggedOutMarker } from "../tokens/loggedOutMarker.js";
import { refreshTokenStore } from "../tokens/refreshTokenStore.js";
import { sessionTenantMarker } from "../tokens/sessionTenantMarker.js";
import { UNAUTH_SNAPSHOT } from "../types.js";
import type { Audience, AuthSnapshot } from "../types.js";

/**
 * Total boot budget for waiting out a rate-limited refresh. A throttled
 * `/auth/refresh` at hard-reload time does NOT mean the session is gone — the
 * refresh credential is usually still valid — so treating it as UNAUTH on the
 * first pass would bounce a signed-in user to /login. A ~10s spinner beats a
 * spurious logout on cold boot.
 */
const BOOT_RATE_LIMIT_BUDGET_MS = 10_000;
const BOOT_RATE_LIMIT_CUSHION_MS = 150;
/**
 * First wait before retrying a refresh that got no verdict: no answer (offline,
 * a dropped connection) or a 5xx (the backend restarting behind the gateway).
 * Each further retry waits twice as long, up to the cap below. A blip at page
 * load shouldn't land a signed-in user on the login page with their session
 * cookie still valid.
 */
const BOOT_NETWORK_RETRY_MS = 1500;
const BOOT_NETWORK_RETRY_MAX_MS = 30_000;

/** Parsed rate-limited error info; null for any other failure. */
function rateLimitInfo(err: unknown): { fastFail: boolean; retryAfterSeconds?: number } | null {
  if (typeof err !== "object" || err === null) return null;
  const e = err as {
    errorCode?: unknown;
    retryAfterSeconds?: unknown;
    isCooldownFastFail?: unknown;
  };
  if (e.errorCode !== REFRESH_RATE_LIMITED) return null;
  return {
    fastFail: e.isCooldownFastFail === true,
    ...(typeof e.retryAfterSeconds === "number" ? { retryAfterSeconds: e.retryAfterSeconds } : {}),
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RefreshOnLoadOpts {
  /**
   * Audience for this app. Selects the bootstrap-skip strategy: tenant
   * audiences can trust the sentinel cookie; platform cannot
   * (see below) and always attempts the refresh.
   */
  aud: Audience;
  /**
   * Called before each wait that follows an attempt with no verdict (network
   * error or 5xx), so the boot surface can say it is still trying. `failures`
   * counts those attempts in a row; `retryNow` ends the current wait and
   * starts the doubling again from the first delay.
   */
  onUnreachable?: (state: UnreachableState) => void;
  /**
   * Keep retrying an attempt with no verdict until the server answers, instead
   * of giving up at the boot budget. For a caller that holds the page and says
   * it is waiting: a backend restart outlasts any budget, and giving up lands
   * a signed-in user on a login form that can't work either. A caller that
   * abandons the restore must leave it off, or the retries outlive it.
   */
  waitForServer?: boolean;
}

export interface UnreachableState {
  failures: number;
  retryNow: () => void;
}

export interface RefreshOnLoadResult {
  snapshot: AuthSnapshot;
}

export async function refreshOnLoad(opts: RefreshOnLoadOpts): Promise<RefreshOnLoadResult> {
  // BRIDGE — written 2026-09-23; remove it (with `has`/`remove`) 30 days after
  // this build reaches production, the longest refresh-token lifetime.
  //
  // Earlier builds kept a copy of the refresh token in localStorage. It is never
  // presented (the cookie is the only credential), and a copy that fell behind
  // the cookie is exactly what signed people out after a browser restart. But
  // until the backend change of 2026-09-22 the sentinel was always a
  // browser-session cookie that a refresh never re-set, while the refresh
  // cookie lasted 7 days. Anyone who restarted their browser since signing in
  // can therefore hold a live refresh cookie and NO sentinel; the old boot
  // reached that cookie only because the stored copy existed. So the copy
  // still counts as a reason to try — cookie-only — and is deleted only after
  // a definite answer (a 200, or a 401 of any code: with no cookie the request
  // is refused before any token lookup). A network error, 5xx or throttle
  // leaves it for the next boot, which would otherwise have no reason to try.
  // Only this audience's key: see `remove`.
  const hadLegacyCopy = refreshTokenStore.has(opts.aud);
  // Bootstrap-skip signal (tenant audiences only): the session sentinel cookie.
  // The backend now sets and clears it together with the refresh cookie and it
  // lives at least as long, so no sentinel means no cookie to refresh with.
  // Platform can't use it: the backend scopes both platform cookies under
  // `/api/v1/_platform`, so the `linkhr_session=1` sentinel is invisible to
  // `document.cookie` on the SPA page at `/`, and skipping here logged a
  // signed-in platform admin out on every reload — so the platform chain
  // always attempts the refresh instead. Cost on a cold unauthenticated boot:
  // one fast 401 from `/_platform/auth/refresh`.
  // Accounting is the same case: it runs on its own host
  // (app.[staging.]accounting.linkco.jo), where the sentinel, scoped to the HR
  // host, is invisible too.
  //
  // Exception: an explicit logout sets `loggedOutMarker` (cleared on the next
  // successful login). Without it, a surviving server-side cookie — platform
  // logout is best-effort and can fail to revoke — would silently log the
  // just-logged-out admin straight back in on the next boot.
  if (loggedOutMarker.isSet()) {
    return { snapshot: UNAUTH_SNAPSHOT };
  }
  const sentinelVisible = opts.aud !== "platform" && opts.aud !== "accounting";
  if (sentinelVisible && !readSessionSentinel() && !hadLegacyCopy) {
    return { snapshot: UNAUTH_SNAPSHOT };
  }
  // Make sure the cross-tab cooldown mirror is listening DURING bootstrap —
  // AuthProvider's channel handlers only install after React mounts, so
  // without this a storage-disabled tab misses mid-boot cooldown broadcasts.
  ensureCooldownMirrorInstalled();
  // Primary attempt + at most ONE real rate-limited retry inside a total boot
  // budget. A 429 at boot is throttling, not an invalid session — waiting out
  // the SHARED cooldown (re-read after every wake: another tab can extend it
  // mid-sleep) keeps a signed-in user out of the /login bounce. Entry-gate
  // fast-fails carry no network cost and do NOT consume the retry. A refresh
  // with no verdict (no answer, or a 5xx) is retried with a doubling wait for
  // as long as the budget allows, or until one comes with `waitForServer`.
  // Every other failure falls through to UNAUTH.
  const bootDeadlineMs = Date.now() + BOOT_RATE_LIMIT_BUDGET_MS;
  let realRateLimitedAttempts = 0;
  let unreachableWaitMs = BOOT_NETWORK_RETRY_MS;
  let unreachableFailures = 0;
  let wake: (() => void) | null = null;
  const retryNow = (): void => {
    unreachableWaitMs = BOOT_NETWORK_RETRY_MS;
    wake?.();
  };
  for (;;) {
    try {
      const { accessToken, effectivePermissions, mustChangePassword } =
        await tryRefreshCrossTabFull();
      // A definite answer: the stored copy has done its one job.
      if (hadLegacyCopy) refreshTokenStore.remove(opts.aud);
      const claims = decodeJwtClaims(accessToken);
      if (claims === null) {
        accessTokenStore.clear();
        return { snapshot: UNAUTH_SNAPSHOT };
      }
      // The refresh cookie is shared across tenant subdomains, so a valid token
      // is NOT evidence that this session belongs to the tenant in the URL:
      // opening `linkco.…` after signing into `acme.…` refreshes acme's session
      // and then renders acme's data under linkco's name. The token is honest;
      // the page would not be. Refuse the restore and let the login screen for
      // the requested tenant do its job. Same shape as the audience check in
      // commitVerifiedSession — a boundary the token alone cannot express.
      const tokenTenantId =
        (claims as typeof claims & { tid?: string | null }).tid ?? claims.tenant_id;
      if (opts.aud !== "platform" && !sessionTenantMarker.allowsRestore(tokenTenantId)) {
        accessTokenStore.clear();
        return { snapshot: UNAUTH_SNAPSHOT };
      }
      scheduleProactiveRefresh();
      const snapshot: AuthSnapshot = {
        isAuthenticated: true,
        // CC-58 puts email + name straight in the JWT — surface identity on
        // the very first paint after a reload instead of a "—" avatar.
        // Tenant audiences later overwrite with the richer /employees/me
        // hydration; platform (which never calls /me) relies on this alone.
        user:
          typeof claims.email === "string" && claims.email.length > 0
            ? {
                id: claims.sub,
                email: claims.email,
                displayName:
                  typeof claims.name === "string" && claims.name.length > 0
                    ? claims.name
                    : (claims.email.split("@")[0] ?? claims.email),
              }
            : null,
        // Present even when `user` is not: the identity block needs an email
        // in the token, the audit-field comparison only needs the subject.
        userId: claims.sub,
        roles: claims.roles,
        // Real backend puts the tenant id in `tid`; MSW uses `tenant_id`. Read
        // either, mirroring login.ts/otp.ts — reading only `tenant_id` here left
        // `tenantId` undefined after a hard refresh against the real backend, so
        // every screen that keys queries off `auth.tenantId ?? "acme"` collapsed
        // onto the "acme" cache namespace (cross-tenant collision).
        tenantId: tokenTenantId,
        aud: claims.aud,
        accessToken,
        // Plan 08.1-02 — perms come from the cross-tab refresh leader. If this
        // tab is the leader, `performRefresh` fetched them; if a follower hit
        // the fresh-token short-circuit, perms are undefined and the BC
        // TOKEN_REFRESHED handler in AuthProvider will hydrate them on the
        // next leader rotation. Either way the gate fails closed during the
        // gap (RESEARCH §Pitfall 2).
        effectivePermissions:
          effectivePermissions !== undefined ? new Set(effectivePermissions) : undefined,
        // Restricted (temporary password): the refresh skipped the permissions
        // fetch, `requireAuth` routes to the password change before the shell
        // renders, and createAppRoot skips the /employees/me hydration.
        mustChangePassword: mustChangePassword === true,
      };
      return { snapshot };
    } catch (err) {
      const code = (err as { errorCode?: unknown } | null)?.errorCode;
      if (
        (code === REFRESH_NETWORK_ERROR || code === REFRESH_SERVER_ERROR) &&
        (opts.waitForServer === true || Date.now() + unreachableWaitMs <= bootDeadlineMs)
      ) {
        unreachableFailures += 1;
        opts.onUnreachable?.({ failures: unreachableFailures, retryNow });
        const waitMs = unreachableWaitMs;
        unreachableWaitMs = Math.min(unreachableWaitMs * 2, BOOT_NETWORK_RETRY_MAX_MS);
        // Up to 30% jitter, so browsers that lost the server together don't
        // all come back at the same instant.
        await new Promise<void>((resolve) => {
          const handle = setTimeout(resolve, waitMs * (1 + Math.random() * 0.3));
          wake = () => {
            clearTimeout(handle);
            resolve();
          };
        });
        wake = null;
        // The caller gave up on this restore and pointed the network at another
        // audience (the global login's mobile restore, abandoned after 5s):
        // a retry now would refresh that audience's session as this one.
        if (getAudience() !== opts.aud) return { snapshot: UNAUTH_SNAPSHOT };
        continue;
      }
      const rl = rateLimitInfo(err);
      if (rl !== null) {
        // Only a REAL network 429 consumes the single retry; an entry-gate
        // fast-fail (cooldown re-published/extended by another tab while this
        // one slept) just means "wait longer", not "attempt spent".
        if (!rl.fastFail) realRateLimitedAttempts += 1;
        if (realRateLimitedAttempts <= 1) {
          // Wait out the CURRENT shared cooldown — re-read fresh each pass so
          // a mid-sleep extension is honored rather than fast-failed into.
          const cooldownRemainingMs = Math.max(0, getRefreshCooldownUntil() - Date.now());
          const headerMs = rl.retryAfterSeconds !== undefined ? rl.retryAfterSeconds * 1000 : 1000;
          const waitMs = Math.max(cooldownRemainingMs, headerMs) + BOOT_RATE_LIMIT_CUSHION_MS;
          if (Date.now() + waitMs <= bootDeadlineMs) {
            await sleep(waitMs);
            continue;
          }
          // Accepted residual: a throttle outlasting the 10s boot budget
          // still lands on UNAUTH/login — boot can't spin forever.
        }
      }
      // Any other error (401, family revoked, a network error or 5xx past the
      // budget, exhausted throttle) → unauthenticated; the coordinator has
      // already posted FAMILY_REVOKED if applicable. Route loaders own redirects.
      const status = (err as { status?: unknown } | null)?.status;
      if (hadLegacyCopy && (status === 401 || status === 403)) {
        // A definite answer from the server (no session, or a session this app
        // may no longer use). Anything else — network, 5xx, throttle — keeps
        // the copy so the next boot tries again.
        refreshTokenStore.remove(opts.aud);
      }
      accessTokenStore.clear();
      return { snapshot: UNAUTH_SNAPSHOT };
    }
  }
}
